import {
  Component,
  OnInit,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Chart } from 'chart.js';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { SitesSelectorComponent } from 'src/app/shared/sites-selector/sites-selector.component';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { RBACService } from '../../../shared/rbac.service';
import moment from 'moment';

interface IRange {
  value: Date[];
  label: string;
}

/**
 * Access to a single welcome card pointer.
 *
 * visible   - the destination is reachable, so the pointer is offered at all.
 * canManage - the user can create, edit or delete there, so the action-oriented wording applies.
 *             When false the pointer uses read-only wording instead.
 */
interface WelcomeItemAccess {
  visible: boolean;
  canManage: boolean;
}

@Component({
  selector: 'app-legacy-dashboard-widget',
  templateUrl: './legacy-dashboard-widget.component.html',
  styleUrls: ['./legacy-dashboard-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LegacyDashboardWidgetComponent implements OnInit, AfterViewInit {
  // date picker var
  datepickerConfig: Partial<BsDatepickerConfig>;

  // Permission flags, one per dashboard chart/feature, resolved in ngOnInit().
  canViewSiteAccess: boolean = false;
  canViewHazards: boolean = false;
  canViewIncidents: boolean = false;
  canViewNetwork: boolean = false;
  // Sub-flags for the connected-network card. Each gates its own button and pie slice so a user
  // with only one of the two permissions does not see the other feature's data.
  canViewContractors: boolean = false;
  canViewEmployees: boolean = false;

  // Whether the active account is on a Premium plan. Drives the welcome pointers (Premium gets a
  // quick start guide; Free gets pointers to its free features). Resolved via planCan on a premium
  // feature, so super admins (who bypass plan gating) also see the Premium pointers.
  isPremiumPlan: boolean = false;

  // Pre-computed permission flags for the welcome card list items, one per destination the pointers
  // link to. Resolved once in ngOnInit() so the template only does property lookups: the dashboard
  // renders several widgets and charts, and calling rbac.userCanAccess() from the template would
  // re-evaluate on every change detection cycle.
  //
  // `visible` gating deliberately mirrors the left-hand navigation (AppComponent.navCan) for the same
  // destinations, so a pointer is only offered when the page it links to is actually reachable. Note
  // that is looser than the chart flags above: the charts need A_VIEW because they render data, while
  // a pointer only needs the page to be reachable, which any action on the feature allows.
  //
  // `canManage` then decides which wording the pointer uses. The original copy was written entirely
  // in the imperative ("Add your employees", "Build forms"), which misleads a view-only user into
  // expecting capabilities they do not have. Each pointer therefore has a read-only variant.
  welcomeCan: {
    sites: WelcomeItemAccess;
    employees: WelcomeItemAccess;
    contractors: WelcomeItemAccess;
    tasks: WelcomeItemAccess;
    accountInductions: WelcomeItemAccess;
    forms: WelcomeItemAccess;
    clientSites: WelcomeItemAccess;
    clientPreQualifications: WelcomeItemAccess;
  } = {
    sites: { visible: false, canManage: false },
    employees: { visible: false, canManage: false },
    contractors: { visible: false, canManage: false },
    tasks: { visible: false, canManage: false },
    accountInductions: { visible: false, canManage: false },
    forms: { visible: false, canManage: false },
    clientSites: { visible: false, canManage: false },
    clientPreQualifications: { visible: false, canManage: false }
  };

  // Whether the active plan's welcome list has at least one visible item. When false, the intro
  // sentence and the list are both hidden so the card never renders a dangling lead-in above an
  // empty bullet list. The rest of the card (heading and mobile app note) still applies to everyone.
  hasWelcomeItems: boolean = false;

  // Whether any visible item on the active plan's list is manageable. Drives the lead-in sentence:
  // "things you can do to get started" and "Quick start guide" both promise setup, which is wrong for
  // a user who can only look at these features.
  hasManageableWelcomeItems: boolean = false;

  // Whether the user holds no portal feature permission at all, meaning a field worker who belongs in
  // the mobile app. Pre-computed for the same reason as the flags above: holdsNoFeaturePermissions()
  // scans the whole permission array, and calling it from the template re-ran that scan on every
  // change detection cycle, which on this chart-heavy page is often.
  hasNoFeaturePermissions: boolean = false;

  // site activities chart vars
  site_activities_chart: Chart;
  site_activities_chart_ref;
  site_activities_chart_data = {
    labels: [],
    data: []
  };

  // site activities chart vars
  hazards_chart: Chart;
  hazards_chart_ref;
  hazards_chart_data = {
    labels: [],
    data: []
  };

  // site activities chart vars
  incidents_chart: Chart;
  incidents_chart_ref;
  incidents_chart_data = {
    labels: [],
    data: []
  };

  // site activities chart vars
  users_chart: Chart;
  users_chart_ref;
  users_chart_data = {
    labels: [],
    data: []
  };

  // Date Range Picker Preset Data
  ranges: IRange[] = [
    {
      value: [
        moment()
          .startOf('day')
          .toDate(),
        moment()
          .endOf('day')
          .toDate()
      ],
      label: 'Today'
    },
    {
      value: [
        moment()
          .subtract(1, 'day')
          .startOf('day')
          .toDate(),
        moment()
          .subtract(1, 'day')
          .endOf('day')
          .toDate()
      ],
      label: 'Yesterday'
    },
    {
      value: [
        moment()
          .startOf('week')
          .toDate(),
        moment()
          .endOf('week')
          .toDate()
      ],
      label: 'This Week'
    },
    {
      value: [
        moment()
          .subtract(7, 'days')
          .startOf('week')
          .toDate(),
        moment()
          .subtract(7, 'days')
          .endOf('week')
          .toDate()
      ],
      label: 'Last Week'
    },
    {
      value: [
        moment()
          .startOf('month')
          .toDate(),
        moment()
          .endOf('month')
          .toDate()
      ],
      label: 'This Month'
    },
    {
      value: [
        moment()
          .subtract(1, 'month')
          .startOf('month')
          .toDate(),
        moment()
          .subtract(1, 'month')
          .endOf('month')
          .toDate()
      ],
      label: 'Last Month'
    },
    {
      value: [
        moment()
          .subtract(2, 'months')
          .startOf('month')
          .toDate(),
        moment()
          .endOf('month')
          .toDate()
      ],
      label: 'Last Three Months'
    },
    {
      value: [
        moment()
          .subtract(5, 'months')
          .startOf('month')
          .toDate(),
        moment()
          .endOf('month')
          .toDate()
      ],
      label: 'Last Six Months'
    },
    {
      value: [
        moment()
          .subtract(11, 'months')
          .startOf('month')
          .toDate(),
        moment()
          .endOf('month')
          .toDate()
      ],
      label: 'Last Twelve Months'
    }
  ];

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // setting the date picker object to select date ranges
    this.datepickerConfig = Object.assign(
      {},
      {
        containerClass: 'theme-orange',
        dateInputFormat: 'DD MMM YYYY',
        rangeInputFormat: 'DD MMM YYYY',
        showWeekNumbers: true,
        adaptivePosition: true,
        ranges: this.ranges
      }
    );

    // Resolve permission flags. Premium charts require BOTH the subscription plan (planCan) and the
    // RBAC view permission. The connected-network chart covers contractors/employees (free), so it
    // is RBAC-gated only.
    this.canViewSiteAccess =
      this.app.planCan(AppService.FEATURES.SITE_ACCESS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REPORTS_SITE_ACCESS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewHazards =
      this.app.planCan(AppService.FEATURES.HAZARDS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_HAZARDS_RISKS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    this.canViewIncidents =
      this.app.planCan(AppService.FEATURES.INCIDENTS) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_REGISTERS_INCIDENTS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      );
    // The connected-network pie chart summarises the account's contractors and employees, both of
    // which are free features. Resolve each sub-permission, then show the card when the user can
    // view either (so it appears for free accounts rather than requiring networked-users). Each
    // button and pie slice is gated by its own flag below.
    //
    // Both are ANDed with accountFeatures so the card disappears on a personal account (SC-301), which
    // has no contractors and exactly one employee. The premium flags above inherit the same rule through
    // planCan(); these two are RBAC-only, so it is stated. Super admins keep full access, matching
    // planCan() and the API.
    const accountFeatures: boolean = this.app.user.is_sitesoft_admin
      || this.app.accountFeaturesAvailable();

    this.canViewContractors = accountFeatures && this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canViewEmployees = accountFeatures && this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_EMPLOYEES,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canViewNetwork = this.canViewContractors || this.canViewEmployees;

    // Premium is inferred from a premium feature being unlocked (SITES). Super admins bypass and
    // are treated as Premium for the purpose of the welcome pointers.
    this.isPremiumPlan = this.app.planCan(AppService.FEATURES.SITES);

    this.hasNoFeaturePermissions = this.rbac.holdsNoFeaturePermissions();

    this.resolveWelcomeItemPermissions();
  }

  /**
   * Resolve the welcome card's per-item permission flags.
   *
   * Kept in one place, and computed once, so the template stays free of permission logic. Each entry
   * uses the same plan and RBAC gating as the matching left-hand navigation item, so the pointers and
   * the menu can never disagree about whether a destination is available.
   */
  private resolveWelcomeItemPermissions(): void {
    const F = RBACService.RBAC_F;
    const A = RBACService.RBAC_A;

    // The usual write set. Holding any one of these means the action-oriented wording is honest.
    const write: string[] = [A.A_CREATE, A.A_EDIT, A.A_DELETE];

    this.welcomeCan = {
      sites: this.resolveItemAccess(
        F.F_SITES,
        write,
        AppService.FEATURES.SITES
      ),
      employees: this.resolveItemAccess(F.F_EMPLOYEES, write),
      contractors: this.resolveItemAccess(F.F_CONTRACTORS, write),
      tasks: this.resolveItemAccess(F.F_TASKS, write),
      accountInductions: this.resolveItemAccess(
        F.F_ACCOUNT_INDUCTIONS,
        write,
        AppService.FEATURES.INDUCTIONS
      ),
      forms: this.resolveItemAccess(
        F.F_FORMS,
        write,
        AppService.FEATURES.FORMS
      ),
      // The client-facing features declare narrower action sets: Client Sites has no create, and
      // Client Pre-Qualifications has neither create nor delete. Asking for a permission the
      // catalogue never defines would leave canManage permanently false, so the write set is
      // trimmed to what each feature actually supports.
      clientSites: this.resolveItemAccess(F.F_CLIENT_SITES, [
        A.A_EDIT,
        A.A_DELETE
      ]),
      clientPreQualifications: this.resolveItemAccess(
        F.F_CLIENT_PRE_QUALIFICATIONS,
        [A.A_EDIT]
      )
    };

    // Only the items belonging to the plan's own list count, since the other list is never rendered.
    const activeItems: WelcomeItemAccess[] = this.isPremiumPlan
      ? [
          this.welcomeCan.sites,
          this.welcomeCan.employees,
          this.welcomeCan.contractors,
          this.welcomeCan.accountInductions,
          this.welcomeCan.forms
        ]
      : [
          this.welcomeCan.employees,
          this.welcomeCan.tasks,
          this.welcomeCan.contractors,
          this.welcomeCan.clientSites,
          this.welcomeCan.clientPreQualifications
        ];

    this.hasWelcomeItems = activeItems.some(
      (item: WelcomeItemAccess): boolean => item.visible
    );
    this.hasManageableWelcomeItems = activeItems.some(
      (item: WelcomeItemAccess): boolean => item.canManage
    );
  }

  /**
   * Resolve whether a welcome pointer is offered, and whether its action-oriented wording applies.
   *
   * @param feature The RBAC feature slug (RBACService.RBAC_F).
   * @param writeActions The actions that count as managing this feature. Trimmed per feature, because
   *                     not every feature declares all of create, edit and delete.
   * @param planFeature The subscription feature that must be unlocked, or null for a free feature.
   */
  private resolveItemAccess(
    feature: string,
    writeActions: string[],
    planFeature: string | null = null
  ): WelcomeItemAccess {
    // The plan gate sits above RBAC: a locked feature is not offered at all, whatever the roles say.
    if (planFeature !== null && !this.app.planCan(planFeature)) {
      return { visible: false, canManage: false };
    }

    const canManage: boolean = this.rbac.userCanAccess(
      writeActions.map(
        (action: string): string => RBACService.getPermission(feature, action)
      )
    );

    const canView: boolean = this.rbac.userCanAccess(
      [RBACService.getPermission(feature, RBACService.RBAC_A.A_VIEW)],
      true
    );

    return { visible: canManage || canView, canManage };
  }

  ngAfterViewInit() {
    // Only initialize charts the user is permitted to see. The canvases are *ngIf-gated
    // by the same flags, so they only exist in the DOM when permitted.
    this.initSiteActivitiesChart();
    this.initHazardsChart();
    this.initIncidentsChart();
    this.initNetworkChart();

    this.refreshStats();
  }

  /**
   * Initialize the site activities (sign in/out) line chart.
   */
  private initSiteActivitiesChart() {
    if (!this.canViewSiteAccess) {
      return;
    }
    this.site_activities_chart_ref = document.getElementById(
      'site_activities_chart_ref'
    );
    this.site_activities_chart = new Chart(this.site_activities_chart_ref, {
      type: 'line',
      data: {
        labels: this.site_activities_chart_data.labels,
        datasets: [
          {
            label:
              this.utils.getLangTerm('contractors.plural', 'Contractors') +
              '/Visitors Per Day',
            borderColor: 'rgb(255, 99, 132)',
            data: this.site_activities_chart_data.data
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  /**
   * Initialize the hazards & risks line chart.
   */
  private initHazardsChart() {
    if (!this.canViewHazards) {
      return;
    }
    this.hazards_chart_ref = document.getElementById('hazards_chart_ref');
    this.hazards_chart = new Chart(this.hazards_chart_ref, {
      type: 'line',
      data: {
        labels: this.hazards_chart_data.labels,
        datasets: [
          {
            label: 'Hazards & Risks Per Day',
            borderColor: 'rgb(255, 99, 132)',
            data: this.hazards_chart_data.data
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  /**
   * Initialize the incidents & near misses line chart.
   */
  private initIncidentsChart() {
    if (!this.canViewIncidents) {
      return;
    }
    this.incidents_chart_ref = document.getElementById('incidents_chart_ref');
    this.incidents_chart = new Chart(this.incidents_chart_ref, {
      type: 'line',
      data: {
        labels: this.incidents_chart_data.labels,
        datasets: [
          {
            label: 'Incidents & Near Misses Per Day',
            borderColor: 'rgb(255, 99, 132)',
            data: this.incidents_chart_data.data
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  /**
   * Initialize the connected network (contractors vs users) pie chart.
   */
  private initNetworkChart() {
    if (!this.canViewNetwork) {
      return;
    }
    this.users_chart_ref = document.getElementById('users_chart_ref');
    this.users_chart = new Chart(this.users_chart_ref, {
      type: 'pie',
      data: {
        labels: this.users_chart_data.labels,
        datasets: [
          {
            backgroundColor: ['rgb(0, 90, 255)', 'rgb(255, 99, 132)'],
            data: this.users_chart_data.data
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  refreshStats() {
    // Nothing on this widget can display account statistics, so do not ask for them.
    //
    // This ran unconditionally, whatever the four permission flags said, and its promise had no
    // rejection handler. On a personal account every panel it feeds is hidden and the request is a
    // guaranteed 403, which surfaced as an unhandled rejection in the console and in Sentry rather than
    // as anything the user could see. The same was already true of any account holding none of these
    // four permissions.
    if (
      !this.canViewSiteAccess &&
      !this.canViewHazards &&
      !this.canViewIncidents &&
      !this.canViewNetwork
    ) {
      return;
    }

    this.api
      .makeRequest(
        'get',
        'v1/dashboard/stats?from=' +
          this.app.filter_date_range[0].getTime() / 1000 +
          '&to=' +
          this.app.filter_date_range[1].getTime() / 1000 +
          '&selected_sites=' +
          this.app.chart_selected_sites.join(',')
      )
      .then((response: any) => {
        const data = response.data || {};

        // Apply each section independently. Each applier is gated by its own
        // permission flag, chart existence, and the presence of the section.
        this.applySiteActivitiesData(data);
        this.applyHazardsData(data);
        this.applyIncidentsData(data);
        this.applyNetworkData(data);
      })
      .catch((): void => {
        // The charts keep their empty state. Nothing here is worth interrupting the dashboard for.
      });
  }

  /**
   * Apply the site activities data to its chart.
   */
  private applySiteActivitiesData(data: any) {
    if (
      !this.canViewSiteAccess ||
      !this.site_activities_chart ||
      !data.site_activities
    ) {
      return;
    }
    this.site_activities_chart_data.labels.length = 0;
    this.site_activities_chart_data.labels.push(...data.site_activities.labels);

    this.site_activities_chart_data.data.length = 0;
    this.site_activities_chart_data.data.push(...data.site_activities.values);

    this.site_activities_chart.update();
  }

  /**
   * Apply the hazards data to its chart.
   */
  private applyHazardsData(data: any) {
    if (!this.canViewHazards || !this.hazards_chart || !data.hazards) {
      return;
    }
    this.hazards_chart_data.labels.length = 0;
    this.hazards_chart_data.labels.push(...data.hazards.labels);

    this.hazards_chart_data.data.length = 0;
    this.hazards_chart_data.data.push(...data.hazards.values);

    this.hazards_chart.update();
  }

  /**
   * Apply the incidents data to its chart.
   */
  private applyIncidentsData(data: any) {
    if (!this.canViewIncidents || !this.incidents_chart || !data.incidents) {
      return;
    }
    this.incidents_chart_data.labels.length = 0;
    this.incidents_chart_data.labels.push(...data.incidents.labels);

    this.incidents_chart_data.data.length = 0;
    this.incidents_chart_data.data.push(...data.incidents.values);

    this.incidents_chart.update();
  }

  /**
   * Apply the connected network data to its chart.
   */
  private applyNetworkData(data: any) {
    if (!this.canViewNetwork || !this.users_chart || !data.users) {
      return;
    }
    this.users_chart_data.labels.length = 0;
    this.users_chart_data.labels.push(...data.users.labels);

    this.users_chart_data.data.length = 0;
    this.users_chart_data.data.push(...data.users.values);

    this.users_chart.update();
  }

  onSelectSites() {
    // show the site selector
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.app.chart_selected_sites,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.app.chart_selected_sites = results;

          this.refreshStats();
        }
      }
    );
  }
}
